import { useLazyQuery, useQuery } from '@apollo/client';
import { Delta } from 'quill/core';
import { useCallback, useMemo } from 'react';
import calculateVersionChanges from './calculateVersionChanges';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';

const GET_VERSION_CHANGES_WITH_CONTENT_HISTORY_FRAGMENT = gql(`
  fragment getVersionChangesWithContent_history on History {
    id
    snapshot
    diff
  }
`);

function getVersionChangesWithContent(
  history: DocumentType<
    typeof GET_VERSION_CHANGES_WITH_CONTENT_HISTORY_FRAGMENT
  >,
) {
  const calculated = calculateVersionChanges(history.diff);
  const composed = new Delta(history.snapshot)
    .compose(new Delta(history.diff))
    .compose(calculated.delta ?? new Delta());

  return { content: composed, allVersionChanges: calculated.versionChanges };
}

const HISTORY_RANGE_QUERY = gql(/** @complexity 2 */ `
  query HistoryRange($postId: ID!, $versionFrom: Int!, $versionTo: Int!) {
    post(id: $postId) {
      id
      history(versionFrom: $versionFrom, versionTo: $versionTo) {
        id
        ...getVersionChangesWithContent_history
      }
    }
  }
`);

export function useLazyHasVersionChanges() {
  const [getHistoryRange, result] = useLazyQuery(HISTORY_RANGE_QUERY, {
    fetchPolicy: 'network-only',
  });

  return [
    useCallback(
      async ({
        postId,
        versionFrom,
        versionTo,
      }: {
        postId: string;
        versionFrom: number;
        versionTo: number;
      }) => {
        const hasDifferentVersions = versionFrom !== versionTo;
        if (!hasDifferentVersions) return false;

        const { data } = await getHistoryRange({
          variables: {
            postId,
            versionFrom,
            versionTo,
          },
        });
        const history = data?.post.history;
        if (!history) return {};

        const { allVersionChanges } = getVersionChangesWithContent(history);
        return allVersionChanges && allVersionChanges.length > 0;
      },
      [getHistoryRange],
    ),
    result,
  ] as const;
}

function useHistoryRange({
  postId,
  versionFrom,
  versionTo,
}: {
  postId: string;
  versionFrom: number;
  versionTo: number;
}) {
  const hasChanges = versionFrom !== versionTo;
  const { data } = useQuery(HISTORY_RANGE_QUERY, {
    variables: {
      postId,
      versionFrom,
      versionTo,
    },
    // if the versions are the same, there is no need to fetch the history range
    skip: !hasChanges,
    fetchPolicy: 'network-only',
  });

  const history = data?.post.history ?? null;

  return useMemo(
    () =>
      history
        ? getVersionChangesWithContent(history)
        : { content: undefined, allVersionChanges: undefined },
    [history],
  );
}
export default useHistoryRange;
